/**
 * /try Client Ingest Orchestration Helpers.
 *
 * WHAT THIS FILE DOES:
 * - Hosts the heavier ingest flows extracted verbatim from WebTryClient:
 *   URL ingest submit, client-audio-first media
 *   upload, media-readiness WebSocket push, signed-URL uploads for PDF and
 *   document files, and share-create retry.
 *
 * WHY THIS EXISTS:
 * - Extracted from src/client/try-client.ts so the orchestrator stays under
 *   the god-module line. No behavior changes.
 *
 * DESIGN NOTE (test contract):
 * - Every helper takes the WebTryClient instance and calls back through its
 *   members (`client.requestJson`, `client.initAndUploadMedia`, ...) so unit
 *   tests that stub instance members keep working unchanged.
 *
 * CALLED BY:
 * - src/client/try-client.ts (WebTryClient methods)
 *
 * @file src/client/try-client-ingest.ts
 */

import type { WebTryClient } from './try-client.js';
import {
  TryMediaIngestPhases,
  TryMediaTranscodeFallbackReasons,
  WebTryError
} from './try-shared.js';
import type {
  IngestResponse,
  MediaUploadStatus,
  ShareResponse,
  TryFileUploadProgress,
  TryMediaClientTranscodeTelemetry,
  TryMediaIngestPhaseEvent
} from './try-shared.js';
import { delay, resolvePdfContentType, uploadFileToSignedUrl, validateHttpUrl } from './try-utils.js';
import { normalizeDocumentUploadContentType } from '../lib/try-document-file.js';
import {
  MediaUploadWsRuntime,
  buildMediaTranscodeTelemetry,
  normalizeCreateShareError,
  shouldRetryCreateShare,
  type TryMediaUploadResult
} from './try-client-transport.js';

/**
 * Submits URL ingest. The server scrapes synchronously, so the response is final.
 */
export async function submitUrlIngest(client: WebTryClient, params: {
  url: string;
  gistLanguage?: string;
  createShare?: boolean;
  forceRefresh?: boolean;
  signal?: AbortSignal;
  speculativeInstantOnly?: boolean;
}): Promise<IngestResponse> {
  validateHttpUrl(params.url);

  const ingestEndpoint = params.forceRefresh === true
    ? '/api/web/ingest/url?force=true'
    : '/api/web/ingest/url';
  return await client.requestJson<IngestResponse>(ingestEndpoint, {
    method: 'POST',
    body: JSON.stringify({
      url: params.url,
      gistLanguage: params.gistLanguage ?? undefined,
      ...(params.createShare ? { createShare: true } : {}),
      ...(params.speculativeInstantOnly === true ? { speculativeInstantOnly: true } : {})
    }),
    signal: params.signal
  });
}

/**
 * Initializes a PDF upload and uploads the file to GCS.
 */
export async function initAndUploadPdf(client: WebTryClient, params: {
  file: File;
  signal?: AbortSignal;
  onUploadProgress?: (progress: TryFileUploadProgress) => void;
}): Promise<{ uploadId: string; fileName: string; contentType: string }> {
  await client.init();

  const contentType = resolvePdfContentType(params.file);
  if (!contentType) {
    throw new WebTryError('invalid_pdf_type');
  }

  const initResponse = await client.requestJson<{ uploadId: string; uploadUrl: string }>(
    '/api/web/ingest/pdf/init',
    {
      method: 'POST',
      body: JSON.stringify({
        contentType,
        contentLength: params.file.size,
        fileName: params.file.name
      }),
      signal: params.signal
    }
  );

  await uploadFileToSignedUrl(initResponse.uploadUrl, params.file, {
    contentType,
    signal: params.signal,
    onProgress: params.onUploadProgress
  });

  return {
    uploadId: initResponse.uploadId,
    fileName: params.file.name,
    contentType
  };
}

/**
 * Initializes a document upload and uploads the file to GCS.
 */
export async function initAndUploadDocument(client: WebTryClient, params: {
  file: File;
  signal?: AbortSignal;
  onUploadProgress?: (progress: TryFileUploadProgress) => void;
}): Promise<{ uploadId: string; fileName: string; contentType: string }> {
  await client.init();

  const contentType = normalizeDocumentUploadContentType(params.file.type, params.file.name);
  if (!contentType) {
    throw new WebTryError('invalid_document_type');
  }

  const initResponse = await client.requestJson<{ uploadId: string; uploadUrl: string }>(
    '/api/web/ingest/document/init',
    {
      method: 'POST',
      body: JSON.stringify({
        contentType,
        contentLength: params.file.size,
        fileName: params.file.name
      }),
      signal: params.signal
    }
  );

  await uploadFileToSignedUrl(initResponse.uploadUrl, params.file, {
    contentType,
    signal: params.signal,
    onProgress: params.onUploadProgress
  });

  return {
    uploadId: initResponse.uploadId,
    fileName: params.file.name,
    contentType
  };
}

/**
 * Uploads media using a client-audio-first strategy with server fallback.
 *
 * Called by:
 * - WebTryClient.initAndUploadMediaClientFirst() (submit + speculation flows).
 *
 * Flow:
 * 1. Attempt client transcode to MP3.
 * 2. On success, upload transcoded MP3.
 * 3. On typed fallback, upload original media file (existing server extraction path).
 */
export async function initAndUploadMediaClientFirst(client: WebTryClient, params: {
  file: File;
  durationMs?: number;
  signal?: AbortSignal;
  onUploadProgress?: (progress: TryFileUploadProgress) => void;
  onPhaseChange?: (event: TryMediaIngestPhaseEvent) => void;
  onTranscodeDecision?: (telemetry: TryMediaClientTranscodeTelemetry) => void;
}): Promise<TryMediaUploadResult> {
  await client.init();

  params.onPhaseChange?.({
    phase: TryMediaIngestPhases.EXTRACTING_AUDIO,
    fallbackReason: null
  });

  const transcodeResult = await client.transcodeMediaClientSide({
    file: params.file,
    signal: params.signal
  });

  if (transcodeResult.status === 'success') {
    const telemetry = buildMediaTranscodeTelemetry({
      originalFile: params.file,
      uploadedFile: transcodeResult.transcodedFile,
      usedClientTranscode: true,
      fallbackReason: null,
      mediaKindHint: transcodeResult.profile.mediaKind,
      originalContentTypeHint: transcodeResult.profile.contentType
    });
    params.onTranscodeDecision?.(telemetry);

    params.onPhaseChange?.({
      phase: TryMediaIngestPhases.COMPRESSING_AUDIO,
      fallbackReason: null
    });
    params.onPhaseChange?.({
      phase: TryMediaIngestPhases.UPLOADING,
      fallbackReason: null
    });

    const upload = await client.initAndUploadMedia({
      file: transcodeResult.transcodedFile,
      durationMs: transcodeResult.durationMs ?? params.durationMs,
      signal: params.signal,
      onUploadProgress: params.onUploadProgress
    });

    return {
      ...upload,
      usedClientTranscode: true,
      fallbackReason: null,
      telemetry
    };
  }

  if (transcodeResult.fallbackReason === TryMediaTranscodeFallbackReasons.ABORTED && params.signal?.aborted) {
    throw new WebTryError('media_transcode_aborted');
  }

  const telemetry = buildMediaTranscodeTelemetry({
    originalFile: params.file,
    uploadedFile: params.file,
    usedClientTranscode: false,
    fallbackReason: transcodeResult.fallbackReason,
    mediaKindHint: transcodeResult.profile?.mediaKind ?? null,
    originalContentTypeHint: transcodeResult.profile?.contentType ?? null
  });
  params.onTranscodeDecision?.(telemetry);

  params.onPhaseChange?.({
    phase: TryMediaIngestPhases.FALLING_BACK,
    fallbackReason: transcodeResult.fallbackReason
  });
  params.onPhaseChange?.({
    phase: TryMediaIngestPhases.UPLOADING,
    fallbackReason: transcodeResult.fallbackReason
  });

  const upload = await client.initAndUploadMedia({
    file: params.file,
    durationMs: params.durationMs,
    signal: params.signal,
    onUploadProgress: params.onUploadProgress
  });

  return {
    ...upload,
    usedClientTranscode: false,
    fallbackReason: transcodeResult.fallbackReason,
    telemetry
  };
}

/**
 * Creates a share using a single /api/share request.
 */
export async function createShareRequest(client: WebTryClient, params: {
  contentId: string;
  gistLanguage?: string;
  signal?: AbortSignal;
}): Promise<ShareResponse> {
  return await client.requestJson<ShareResponse>('/api/share', {
    method: 'POST',
    body: JSON.stringify({
      contentId: params.contentId,
      language: params.gistLanguage ?? undefined
    }),
    signal: params.signal
  });
}

/**
 * Retries immediate share creation for short-lived submit→read races.
 */
export async function createShareRequestWithRetry(client: WebTryClient, params: {
  contentId: string;
  gistLanguage?: string;
}): Promise<ShareResponse> {
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= client.shareCreateConfig.MAX_ATTEMPTS; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), client.shareCreateConfig.REQUEST_TIMEOUT_MS);

    try {
      return await createShareRequest(client, {
        contentId: params.contentId,
        gistLanguage: params.gistLanguage,
        signal: controller.signal
      });
    } catch (error) {
      lastError = error;
      if (!shouldRetryCreateShare(error) || attempt >= client.shareCreateConfig.MAX_ATTEMPTS) {
        break;
      }
      // A-011: Exponential backoff — 500, 1000, 2000, 4000, 4000, ...
      const backoff = Math.min(
        client.shareCreateConfig.RETRY_DELAY_MS * Math.pow(2, attempt - 1),
        client.shareCreateConfig.MAX_RETRY_DELAY_MS
      );
      await delay(backoff);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  throw normalizeCreateShareError(lastError);
}

/**
 * Attempts to receive media readiness via WebSocket push.
 *
 * Returns the terminal MediaUploadStatus on success, or null if WS
 * fails to connect or times out (caller should fall back to polling).
 *
 * Timeouts:
 * - 3s to connect.
 * - 30s to receive a terminal event after connecting.
 */
export function waitForMediaReadyViaWs(
  uploadId: string,
  credentials: { uploadWsUrl: string; uploadWsToken: string }
): Promise<MediaUploadStatus | null> {
  return new Promise<MediaUploadStatus | null>((resolve, reject) => {
    let resolved = false;
    let ws: WebSocket | null = null;
    let closeFallbackTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearCloseFallbackTimeout = () => {
      if (closeFallbackTimeout) {
        clearTimeout(closeFallbackTimeout);
        closeFallbackTimeout = null;
      }
    };

    const done = (result: MediaUploadStatus | null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(connectTimeout);
      if (terminalTimeout) clearTimeout(terminalTimeout);
      clearCloseFallbackTimeout();
      try { ws?.close(); } catch { /* best-effort */ }
      resolve(result);
    };

    // Connect timeout: 3s.
    const connectTimeout = setTimeout(() => done(null), MediaUploadWsRuntime.CONNECT_TIMEOUT_MS);

    // Terminal wait timeout: 30s after connect.
    let terminalTimeout: ReturnType<typeof setTimeout> | null = null;

    try {
      const url = `${credentials.uploadWsUrl}?token=${encodeURIComponent(credentials.uploadWsToken)}`;
      ws = new WebSocket(url);
    } catch {
      clearTimeout(connectTimeout);
      done(null);
      return;
    }

    ws.onopen = () => {
      terminalTimeout = setTimeout(() => done(null), MediaUploadWsRuntime.TERMINAL_EVENT_TIMEOUT_MS);
    };

    ws.onmessage = (event) => {
      if (resolved) return;
      clearCloseFallbackTimeout();
      try {
        const data = JSON.parse(String(event.data)) as {
          type?: string;
          status?: string;
          audioFileUri?: string;
          durationMs?: number;
          error?: string;
        };

        if (data.type === 'upload_ready' && data.status === 'ready') {
          if (terminalTimeout) clearTimeout(terminalTimeout);
          done({
            status: 'ready',
            audioFileUri: data.audioFileUri ?? null,
            durationMs: data.durationMs ?? null
          });
        } else if (data.type === 'upload_error' && data.status === 'error') {
          if (terminalTimeout) clearTimeout(terminalTimeout);
          // Terminal error — reject so the caller can propagate the WebTryError.
          resolved = true;
          try { ws?.close(); } catch { /* best-effort */ }
          reject(new WebTryError(data.error ?? 'media_extraction_failed'));
          return;
        }
      } catch {
        if (terminalTimeout) clearTimeout(terminalTimeout);
        // Parse error — fall back to polling.
        done(null);
      }
    };

    ws.onerror = () => {
      done(null);
    };

    ws.onclose = () => {
      if (resolved) return;
      clearTimeout(connectTimeout);
      if (terminalTimeout) clearTimeout(terminalTimeout);
      clearCloseFallbackTimeout();
      closeFallbackTimeout = setTimeout(
        () => done(null),
        MediaUploadWsRuntime.CLOSE_FALLBACK_GRACE_MS
      );
    };
  });
}

/**
 * Stores WS credentials with TTL-based cleanup and a fixed-size cap.
 */
export function storeMediaUploadWsCredentials(
  client: WebTryClient,
  uploadId: string,
  credentials: { uploadWsUrl: string; uploadWsToken: string }
): void {
  client.mediaUploadWsCredentials.set(uploadId, credentials);
  clearMediaUploadWsCredentialCleanupTimer(client, uploadId);

  const cleanupTimer = setTimeout(() => {
    client.mediaUploadWsCredentials.delete(uploadId);
    client.mediaUploadWsCredentialCleanupTimers.delete(uploadId);
  }, MediaUploadWsRuntime.CREDENTIAL_TTL_MS);
  client.mediaUploadWsCredentialCleanupTimers.set(uploadId, cleanupTimer);

  while (client.mediaUploadWsCredentials.size > MediaUploadWsRuntime.MAX_CACHED_CREDENTIALS) {
    const oldestUploadId = client.mediaUploadWsCredentials.keys().next().value as string | undefined;
    if (!oldestUploadId) {
      break;
    }
    client.mediaUploadWsCredentials.delete(oldestUploadId);
    clearMediaUploadWsCredentialCleanupTimer(client, oldestUploadId);
  }
}

/**
 * Reads and removes WS credentials for a specific uploadId.
 */
export function takeMediaUploadWsCredentials(
  client: WebTryClient,
  uploadId: string
): { uploadWsUrl: string; uploadWsToken: string } | null {
  const credentials = client.mediaUploadWsCredentials.get(uploadId) ?? null;
  if (!credentials) {
    return null;
  }

  client.mediaUploadWsCredentials.delete(uploadId);
  clearMediaUploadWsCredentialCleanupTimer(client, uploadId);
  return credentials;
}

export function clearMediaUploadWsCredentialCleanupTimer(client: WebTryClient, uploadId: string): void {
  const cleanupTimer = client.mediaUploadWsCredentialCleanupTimers.get(uploadId);
  if (cleanupTimer) {
    clearTimeout(cleanupTimer);
    client.mediaUploadWsCredentialCleanupTimers.delete(uploadId);
  }
}
